import React, { useState, useEffect } from 'react';
import { FaPlus, FaEdit, FaTrash, FaEye, FaEyeSlash, FaImage, FaLink, FaSearch, FaVideo, FaUpload, FaTimes, FaLayerGroup, FaChevronDown, FaGlobe } from 'react-icons/fa';
import ConfirmModal from '../ConfirmModal';
import { db, storage } from '../../firebase';
import { collection, onSnapshot, query, addDoc, deleteDoc, doc, updateDoc, orderBy, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';

// ISO to Country Name mapping
const isoToCountryName = {
    'IN': 'India',
    'BE': 'Belgium',
    'US': 'USA',
    'DE': 'Germany',
    'ES': 'Spain',
    'FR': 'France',
    'NL': 'Netherlands'
};

const AdsModule = ({ selectedCountry }) => {
    const [ads, setAds] = useState([]);
    const [groups, setGroups] = useState([]);
    const [isCreating, setIsCreating] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [editingAd, setEditingAd] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [uploadedFile, setUploadedFile] = useState(null);
    const [adFrequency, setAdFrequency] = useState(5); // Show ad after every X posts
    const [groupSearchTerm, setGroupSearchTerm] = useState('');
    const [isGroupDropdownOpen, setIsGroupDropdownOpen] = useState(false);
    const [masterCategoryItems, setMasterCategoryItems] = useState([]);
    const [confirmConfig, setConfirmConfig] = useState({ isOpen: false, title: '', message: '', type: 'danger', singleButton: false, onConfirm: null });
    const [confirmModalData, setConfirmModalData] = useState({ isOpen: false, adId: null }); // Legacy - will refactor if needed, and also for deletion

    // Form state
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        mediaUrl: '',
        mediaType: 'image', // 'image' or 'video'
        redirectUrl: '',
        isActive: true,
        country: 'India',
        targetGroupId: 'All' // Default to all groups
    });

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (isGroupDropdownOpen && !event.target.closest('.group-dropdown-container')) {
                setIsGroupDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isGroupDropdownOpen]);

    useEffect(() => {
        // Fetch Ads from Firestore
        const q = query(collection(db, 'ads'), orderBy('createdAt', 'desc'));
        const unsubAds = onSnapshot(q, (snapshot) => {
            const adsData = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            setAds(adsData);
        });

        // Fetch Master Items for translation fallback lookup
        const unsubItems = onSnapshot(collection(db, 'master_category_items'), (snapshot) => {
            const itemsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setMasterCategoryItems(itemsData);
        });

        // Fetch Groups for targeting selection
        const unsubGroups = onSnapshot(collection(db, 'groups'), (snapshot) => {
            const groupsData = snapshot.docs.map(doc => {
                const data = doc.data();
                const id = doc.id;
                
                // Extract ISO code from ID (e.g., BE_addictions...) or field
                const isoCode = id.split('_')[0].toUpperCase();
                const detectedCountry = isoToCountryName[isoCode] || data.country || 'Global';

                // Resolve Display Name from translations in the group itself
                const groupTransVal = data.translations?.en || data.translations?.in || data.translations?.on;
                let resolvedName = (typeof groupTransVal === 'object' ? groupTransVal.name : groupTransVal) || data.name;

                // Fallback to Master Category Items if name still looks like an ID or is missing
                if (!resolvedName || /^[a-zA-Z0-9]{15,}$/.test(resolvedName)) {
                    const strippedId = id.includes('_') ? id.substring(id.indexOf('_') + 1) : id;
                    const masterItem = masterCategoryItems.find(mi => 
                        mi.id === id || 
                        mi.id === strippedId || 
                        mi.id === data.itemId || 
                        mi.id === data.topicId || 
                        mi.id === data.categoryId
                    );
                    
                    if (masterItem) {
                        const masterTransVal = masterItem.translations?.en || masterItem.translations?.in || masterItem.translations?.on;
                        resolvedName = (typeof masterTransVal === 'object' ? masterTransVal.name : masterTransVal) || masterItem.name;
                    }
                }

                return {
                    id: id,
                    name: resolvedName || id.replace(/_/g, ' '),
                    country: detectedCountry
                };
            });
            setGroups(groupsData);
        });

        return () => {
            unsubAds();
            unsubGroups();
            unsubItems();
        };
    }, [masterCategoryItems]);

    // Filter ads by country and search
    const filteredAds = ads.filter(ad => {
        const matchesCountry = selectedCountry === 'All' || ad.country === selectedCountry;
        const matchesSearch = (ad.title || '').toLowerCase().includes(searchTerm.toLowerCase());
        return matchesCountry && matchesSearch;
    });

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleFileUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // Validate file type
        const validImageTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
        const validVideoTypes = ['video/mp4', 'video/webm', 'video/ogg', 'video/quicktime'];

        const isImage = validImageTypes.includes(file.type);
        const isVideo = validVideoTypes.includes(file.type);

        if (!isImage && !isVideo) {
            setConfirmConfig({
                isOpen: true,
                title: "Invalid File Type",
                message: "Please upload a valid image (JPG, PNG, GIF, WebP) or video (MP4, WebM, OGG, MOV).",
                type: "warning",
                singleButton: true
            });
            return;
        }

        // Validate file size (max 50MB for videos, 5MB for images)
        const maxSize = isVideo ? 50 * 1024 * 1024 : 5 * 1024 * 1024;
        if (file.size > maxSize) {
            setConfirmConfig({
                isOpen: true,
                title: "File Too Large",
                message: `File size should be less than ${isVideo ? '50MB' : '5MB'}.`,
                type: "warning",
                singleButton: true
            });
            return;
        }

        setUploadedFile(file);
        setFormData(prev => ({
            ...prev,
            mediaType: isVideo ? 'video' : 'image',
            mediaUrl: URL.createObjectURL(file) // Local preview only
        }));
    };

    const removeUploadedFile = () => {
        setUploadedFile(null);
        setFormData(prev => ({
            ...prev,
            mediaUrl: '',
            mediaType: 'image'
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsUploading(true);

        try {
            let finalMediaUrl = formData.mediaUrl;

            // If a file was uploaded, upload to Firebase Storage
            if (uploadedFile) {
                const storageRef = ref(storage, `ads/${Date.now()}_${uploadedFile.name}`);
                const snapshot = await uploadBytes(storageRef, uploadedFile);
                finalMediaUrl = await getDownloadURL(snapshot.ref);
            }

            const adData = {
                ...formData,
                mediaUrl: finalMediaUrl,
                updatedAt: serverTimestamp()
            };

            if (editingAd) {
                await updateDoc(doc(db, 'ads', editingAd.id), adData);
            } else {
                await addDoc(collection(db, 'ads'), {
                    ...adData,
                    clicks: 0,
                    impressions: 0,
                    createdAt: serverTimestamp()
                });
            }

            setFormData({
                title: '',
                description: '',
                mediaUrl: '',
                mediaType: 'image',
                redirectUrl: '',
                isActive: true,
                country: 'India',
                targetGroupId: 'All'
            });
            setUploadedFile(null);
            setIsCreating(false);
            setEditingAd(null);
            setConfirmConfig({
                isOpen: true,
                title: editingAd ? "Ad Updated" : "Ad Created",
                message: editingAd ? "Advertisement details have been successfully updated." : "Your new advertisement has been launched.",
                type: "success",
                singleButton: true
            });
        } catch (error) {
            console.error("Error saving ad:", error);
            setConfirmConfig({
                isOpen: true,
                title: "Save Error",
                message: "Could not save the ad: " + error.message,
                type: "danger",
                singleButton: true
            });
        } finally {
            setIsUploading(false);
        }
    };

    const handleEdit = (ad) => {
        setEditingAd(ad);
        setFormData({
            title: ad.title || '',
            description: ad.description || '',
            mediaUrl: ad.mediaUrl || '',
            mediaType: ad.mediaType || 'image',
            redirectUrl: ad.redirectUrl || '',
            isActive: ad.isActive ?? true,
            country: ad.country || 'India',
            targetGroupId: ad.targetGroupId || 'All'
        });
        setIsCreating(true);
    };

    const handleDelete = async (id) => {
        try {
            await deleteDoc(doc(db, 'ads', id));
        } catch (error) {
            console.error("Error deleting ad:", error);
        } finally {
            setConfirmModalData({ isOpen: false, adId: null });
        }
    };

    const toggleAdStatus = async (ad) => {
        try {
            await updateDoc(doc(db, 'ads', ad.id), {
                isActive: !ad.isActive
            });
        } catch (error) {
            console.error("Error toggling status:", error);
            setConfirmConfig({
                isOpen: true,
                title: "Status Update Error",
                message: "Failed to update ad status: " + error.message,
                type: "danger",
                singleButton: true
            });
        }
    };

    const handleCancel = () => {
        setIsCreating(false);
        setEditingAd(null);
        setUploadedFile(null);
        setFormData({
            title: '',
            description: '',
            mediaUrl: '',
            mediaType: 'image',
            redirectUrl: '',
            isActive: true,
            country: 'India',
            targetGroupId: 'All'
        });
    };

    // Stats calculation
    const activeAds = ads.filter(ad => ad.isActive).length;
    const totalClicks = ads.reduce((sum, ad) => sum + ad.clicks, 0);
    const totalImpressions = ads.reduce((sum, ad) => sum + ad.impressions, 0);
    const avgCTR = totalImpressions > 0 ? ((totalClicks / totalImpressions) * 100).toFixed(2) : 0;

    if (isCreating) {
        return (
            <div className="content-section fade-in">
                <div className="content-header">
                    <div className="header-with-back">
                        <button className="back-btn" onClick={handleCancel}>← Back to Ads</button>
                        <h2 className="content-title">{editingAd ? 'Edit Ad' : 'Create New Ad'}</h2>
                    </div>
                </div>

                <div className="table-container" style={{ marginTop: '24px' }}>
                    <form onSubmit={handleSubmit} style={{ maxWidth: '800px' }}>
                        <div style={{ display: 'grid', gap: '24px' }}>
                            {/* Ad Title */}
                            <div>
                                <label style={{
                                    display: 'block',
                                    marginBottom: '8px',
                                    fontWeight: 600,
                                    color: 'var(--text-primary)'
                                }}>
                                    Ad Title *
                                </label>
                                <input
                                    type="text"
                                    name="title"
                                    value={formData.title}
                                    onChange={handleInputChange}
                                    required
                                    placeholder="Enter ad title"
                                    style={{
                                        width: '100%',
                                        padding: '12px 16px',
                                        borderRadius: '12px',
                                        border: '1px solid var(--border-light)',
                                        background: 'var(--bg-secondary)',
                                        color: 'var(--text-primary)',
                                        fontSize: '14px'
                                    }}
                                />
                            </div>

                            {/* Ad Description */}
                            <div>
                                <label style={{
                                    display: 'block',
                                    marginBottom: '8px',
                                    fontWeight: 600,
                                    color: 'var(--text-primary)'
                                }}>
                                    Ad Description *
                                </label>
                                <textarea
                                    name="description"
                                    value={formData.description}
                                    onChange={handleInputChange}
                                    required
                                    placeholder="Enter a detailed description of your ad (e.g., special offers, features, benefits)"
                                    rows="4"
                                    maxLength="500"
                                    style={{
                                        width: '100%',
                                        padding: '12px 16px',
                                        borderRadius: '12px',
                                        border: '1px solid var(--border-light)',
                                        background: 'var(--bg-secondary)',
                                        color: 'var(--text-primary)',
                                        fontSize: '14px',
                                        fontFamily: 'inherit',
                                        resize: 'vertical',
                                        minHeight: '100px'
                                    }}
                                />
                                <div style={{
                                    textAlign: 'right',
                                    fontSize: '12px',
                                    color: 'var(--text-tertiary)',
                                    marginTop: '4px'
                                }}>
                                    {formData.description.length}/500 characters
                                </div>
                            </div>

                            {/* Media Upload Section */}
                            <div>
                                <label style={{
                                    display: 'block',
                                    marginBottom: '12px',
                                    fontWeight: 600,
                                    color: 'var(--text-primary)'
                                }}>
                                    <FaImage style={{ marginRight: '8px' }} />
                                    Ad Media (Photo or Video) *
                                </label>

                                <div style={{
                                    display: 'flex',
                                    gap: '8px',
                                    marginBottom: '16px',
                                    padding: '10px 0',
                                    borderBottom: '2px solid var(--primary-blue)',
                                    width: 'fit-content',
                                    color: 'var(--primary-blue)',
                                    fontWeight: 700
                                }}>
                                    <FaUpload style={{ marginRight: '6px' }} />
                                    Media Upload
                                </div>

                                <div style={{
                                    border: '2px dashed var(--border-light)',
                                    borderRadius: '12px',
                                    padding: '32px',
                                    textAlign: 'center',
                                    background: 'var(--bg-secondary)',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s'
                                }}
                                    onDragOver={(e) => {
                                        e.preventDefault();
                                        e.currentTarget.style.borderColor = 'var(--primary-blue)';
                                        e.currentTarget.style.background = 'rgba(79, 172, 254, 0.05)';
                                    }}
                                    onDragLeave={(e) => {
                                        e.currentTarget.style.borderColor = 'var(--border-light)';
                                        e.currentTarget.style.background = 'var(--bg-secondary)';
                                    }}
                                    onDrop={(e) => {
                                        e.preventDefault();
                                        e.currentTarget.style.borderColor = 'var(--border-light)';
                                        e.currentTarget.style.background = 'var(--bg-secondary)';
                                        const file = e.dataTransfer.files[0];
                                        if (file) {
                                            const fakeEvent = { target: { files: [file] } };
                                            handleFileUpload(fakeEvent);
                                        }
                                    }}
                                >
                                    <input
                                        type="file"
                                        id="mediaUpload"
                                        accept="image/*,video/*"
                                        onChange={handleFileUpload}
                                        style={{ display: 'none' }}
                                    />
                                    <label htmlFor="mediaUpload" style={{ cursor: 'pointer', display: 'block' }}>
                                        <div style={{ fontSize: '48px', color: 'var(--primary-blue)', marginBottom: '12px' }}>
                                            <FaUpload />
                                        </div>
                                        <div style={{ fontSize: '16px', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '8px' }}>
                                            Click to upload or drag and drop
                                        </div>
                                        <div style={{ fontSize: '13px', color: 'var(--text-tertiary)' }}>
                                            <div>📷 Images: JPG, PNG, GIF, WebP (max 5MB)</div>
                                            <div>🎥 Videos: MP4, WebM, OGG, MOV (max 50MB)</div>
                                        </div>
                                    </label>
                                </div>

                                {/* Preview */}
                                {formData.mediaUrl && (
                                    <div style={{
                                        marginTop: '16px',
                                        borderRadius: '12px',
                                        overflow: 'hidden',
                                        border: '1px solid var(--border-light)',
                                        position: 'relative'
                                    }}>
                                        {uploadedFile && (
                                            <button
                                                type="button"
                                                onClick={removeUploadedFile}
                                                style={{
                                                    position: 'absolute',
                                                    top: '8px',
                                                    right: '8px',
                                                    background: 'rgba(239, 68, 68, 0.9)',
                                                    color: 'white',
                                                    border: 'none',
                                                    borderRadius: '50%',
                                                    width: '32px',
                                                    height: '32px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    cursor: 'pointer',
                                                    zIndex: 10,
                                                    fontSize: '14px'
                                                }}
                                                title="Remove file"
                                            >
                                                <FaTimes />
                                            </button>
                                        )}
                                        {formData.mediaType === 'video' ? (
                                            <video
                                                src={formData.mediaUrl}
                                                controls
                                                style={{ width: '100%', maxHeight: '300px', objectFit: 'contain', background: '#000' }}
                                                onError={(e) => e.target.style.display = 'none'}
                                            />
                                        ) : (
                                            <img
                                                src={formData.mediaUrl}
                                                alt="Preview"
                                                style={{ width: '100%', maxHeight: '300px', objectFit: 'cover' }}
                                                onError={(e) => e.target.style.display = 'none'}
                                            />
                                        )}
                                        {uploadedFile && (
                                            <div style={{
                                                padding: '12px',
                                                background: 'var(--bg-secondary)',
                                                borderTop: '1px solid var(--border-light)',
                                                fontSize: '12px',
                                                color: 'var(--text-secondary)'
                                            }}>
                                                📎 {uploadedFile.name} ({(uploadedFile.size / 1024 / 1024).toFixed(2)} MB)
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* Redirect URL */}
                            <div>
                                <label style={{
                                    display: 'block',
                                    marginBottom: '8px',
                                    fontWeight: 600,
                                    color: 'var(--text-primary)'
                                }}>
                                    <FaLink style={{ marginRight: '8px' }} />
                                    Redirect URL *
                                </label>
                                <input
                                    type="url"
                                    name="redirectUrl"
                                    value={formData.redirectUrl}
                                    onChange={handleInputChange}
                                    required
                                    placeholder="https://example.com/landing-page"
                                    style={{
                                        width: '100%',
                                        padding: '12px 16px',
                                        borderRadius: '12px',
                                        border: '1px solid var(--border-light)',
                                        background: 'var(--bg-secondary)',
                                        color: 'var(--text-primary)',
                                        fontSize: '14px'
                                    }}
                                />
                            </div>

                            {/* Country */}
                            <div>
                                <label style={{
                                    display: 'block',
                                    marginBottom: '8px',
                                    fontWeight: 600,
                                    color: 'var(--text-primary)'
                                }}>
                                    Target Country *
                                </label>
                                <select
                                    name="country"
                                    value={formData.country}
                                    onChange={handleInputChange}
                                    required
                                    style={{
                                        width: '100%',
                                        padding: '12px 16px',
                                        borderRadius: '12px',
                                        border: '1px solid var(--border-light)',
                                        background: 'var(--bg-secondary)',
                                        color: 'var(--text-primary)',
                                        fontSize: '14px'
                                    }}
                                >
                                    <option value="India">India</option>
                                    <option value="German">German</option>
                                    <option value="Spanish">Spanish</option>
                                    <option value="French">French</option>
                                    <option value="Nederland">Nederland</option>
                                </select>
                            </div>

                            {/* Group Targeting */}
                            <div className="group-dropdown-container" style={{ position: 'relative' }}>
                                <label style={{
                                    display: 'block',
                                    marginBottom: '8px',
                                    fontWeight: 600,
                                    color: 'var(--text-primary)'
                                }}>
                                    <FaLayerGroup style={{ marginRight: '8px' }} />
                                    Target Community *
                                </label>
                                
                                {/* Custom Searchable Dropdown */}
                                <div 
                                    onClick={() => setIsGroupDropdownOpen(!isGroupDropdownOpen)}
                                    style={{
                                        width: '100%',
                                        padding: '12px 16px',
                                        borderRadius: '12px',
                                        border: '1px solid var(--border-light)',
                                        background: 'var(--bg-secondary)',
                                        color: 'var(--text-primary)',
                                        fontSize: '14px',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center'
                                    }}
                                >
                                    <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        {formData.targetGroupId === 'All' ? (
                                            <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                <FaGlobe style={{ color: 'var(--primary-blue)' }} /> All Communities
                                            </span>
                                        ) : (() => {
                                            const selG = groups.find(g => g.id === formData.targetGroupId);
                                            return selG ? (
                                                <>
                                                    <span style={{ fontWeight: 600 }}>{selG.name}</span>
                                                    <span style={{ 
                                                        fontSize: '11px', 
                                                        color: 'var(--text-tertiary)', 
                                                        background: 'var(--bg-secondary)', 
                                                        padding: '2px 8px', 
                                                        borderRadius: '6px',
                                                        border: '1px solid var(--border-light)',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '4px'
                                                    }}>
                                                        <FaGlobe size={10} /> {selG.country}
                                                    </span>
                                                </>
                                            ) : 'Select Community';
                                        })()}
                                    </span>
                                    <FaChevronDown style={{ 
                                        fontSize: '12px', 
                                        color: 'var(--text-tertiary)',
                                        transform: isGroupDropdownOpen ? 'rotate(180deg)' : 'none',
                                        transition: 'transform 0.2s'
                                    }} />
                                </div>

                                {isGroupDropdownOpen && (
                                    <div style={{
                                        position: 'absolute',
                                        top: '100%',
                                        left: 0,
                                        right: 0,
                                        zIndex: 1000,
                                        marginTop: '12px',
                                        background: 'var(--bg-card)',
                                        border: '1px solid var(--border-light)',
                                        borderRadius: '20px',
                                        boxShadow: '0 20px 50px rgba(0, 0, 0, 0.15)',
                                        height: '400px', // Large "half-screen" feel
                                        display: 'flex',
                                        flexDirection: 'column',
                                        overflow: 'hidden',
                                        animation: 'slideDown 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                                    }}>
                                        <style>{`
                                            @keyframes slideDown {
                                                from { opacity: 0; transform: translateY(-10px); }
                                                to { opacity: 1; transform: translateY(0); }
                                            }
                                            .custom-scrollbar::-webkit-scrollbar {
                                                width: 6px;
                                            }
                                            .custom-scrollbar::-webkit-scrollbar-track {
                                                background: transparent;
                                            }
                                            .custom-scrollbar::-webkit-scrollbar-thumb {
                                                background: var(--primary-blue);
                                                border-radius: 10px;
                                                opacity: 0.5;
                                            }
                                            .custom-scrollbar::-webkit-scrollbar-thumb:hover {
                                                background: var(--secondary-blue);
                                            }
                                        `}</style>
                                        <div style={{ padding: '12px', borderBottom: '1px solid var(--border-light)' }}>
                                            <div style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '8px',
                                                background: 'var(--bg-secondary)',
                                                padding: '8px 12px',
                                                borderRadius: '8px',
                                                border: '1px solid var(--border-light)'
                                            }}>
                                                <FaSearch style={{ color: 'var(--text-tertiary)', fontSize: '13px' }} />
                                                <input 
                                                    type="text" 
                                                    placeholder="Search groups or countries..."
                                                    value={groupSearchTerm}
                                                    onChange={(e) => setGroupSearchTerm(e.target.value)}
                                                    onClick={(e) => e.stopPropagation()}
                                                    style={{
                                                        border: 'none',
                                                        background: 'transparent',
                                                        color: 'var(--text-primary)',
                                                        fontSize: '13px',
                                                        outline: 'none',
                                                        width: '100%'
                                                    }}
                                                />
                                            </div>
                                        </div>
                                        <div style={{ overflowY: 'auto', flex: 1 }} className="custom-scrollbar">
                                            {/* All Communities Option */}
                                            {('All Communities'.toLowerCase().includes(groupSearchTerm.toLowerCase()) || groupSearchTerm === '') && (
                                                <div 
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setFormData(prev => ({ ...prev, targetGroupId: 'All' }));
                                                        setIsGroupDropdownOpen(false);
                                                        setGroupSearchTerm('');
                                                    }}
                                                    style={{
                                                        padding: '10px 16px',
                                                        cursor: 'pointer',
                                                        background: formData.targetGroupId === 'All' ? 'var(--primary-light)' : 'transparent',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '8px',
                                                        fontSize: '14px',
                                                        color: formData.targetGroupId === 'All' ? 'var(--primary-blue)' : 'var(--text-primary)',
                                                        transition: 'background 0.2s'
                                                    }}
                                                    onMouseEnter={(e) => e.currentTarget.style.background = 'var(--bg-secondary)'}
                                                    onMouseLeave={(e) => e.currentTarget.style.background = formData.targetGroupId === 'All' ? 'var(--primary-light)' : 'transparent'}
                                                >
                                                    🌍 All Communities
                                                </div>
                                            )}
                                            
                                            {groups
                                                .filter(g => 
                                                    g.name.toLowerCase().includes(groupSearchTerm.toLowerCase()) || 
                                                    g.country.toLowerCase().includes(groupSearchTerm.toLowerCase())
                                                )
                                                .map(g => (
                                                    <div 
                                                        key={g.id}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setFormData(prev => ({ ...prev, targetGroupId: g.id }));
                                                            setIsGroupDropdownOpen(false);
                                                            setGroupSearchTerm('');
                                                        }}
                                                        style={{
                                                            padding: '10px 16px',
                                                            cursor: 'pointer',
                                                            background: formData.targetGroupId === g.id ? 'var(--primary-light)' : 'transparent',
                                                            display: 'flex',
                                                            flexDirection: 'column',
                                                            gap: '2px',
                                                            fontSize: '14px',
                                                            color: formData.targetGroupId === g.id ? 'var(--primary-blue)' : 'var(--text-primary)',
                                                            transition: 'background 0.2s',
                                                            borderBottom: '1px solid var(--border-light)'
                                                        }}
                                                        onMouseEnter={(e) => e.currentTarget.style.background = 'var(--bg-secondary)'}
                                                        onMouseLeave={(e) => e.currentTarget.style.background = formData.targetGroupId === g.id ? 'var(--primary-light)' : 'transparent'}
                                                    >
                                                        <div style={{ fontWeight: 600 }}>{g.name}</div>
                                                        <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                            <FaGlobe size={10} /> {g.country}
                                                        </div>
                                                    </div>
                                                ))}
                                            
                                            {groups.filter(g => 
                                                g.name.toLowerCase().includes(groupSearchTerm.toLowerCase()) || 
                                                g.country.toLowerCase().includes(groupSearchTerm.toLowerCase())
                                            ).length === 0 && groupSearchTerm !== '' && (
                                                <div style={{ padding: '20px', textAlign: 'center', color: 'var(--text-tertiary)', fontSize: '13px' }}>
                                                    No communities found for "{groupSearchTerm}"
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}
                                <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', marginTop: '4px' }}>
                                    Select which group this ad should appear in.
                                </div>
                            </div>


                            {/* Active Status */}
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '12px',
                                padding: '16px',
                                background: 'var(--bg-secondary)',
                                borderRadius: '12px',
                                border: '1px solid var(--border-light)'
                            }}>
                                <input
                                    type="checkbox"
                                    name="isActive"
                                    id="isActive"
                                    checked={formData.isActive}
                                    onChange={handleInputChange}
                                    style={{ width: '20px', height: '20px', cursor: 'pointer' }}
                                />
                                <label htmlFor="isActive" style={{
                                    fontWeight: 600,
                                    color: 'var(--text-primary)',
                                    cursor: 'pointer',
                                    margin: 0
                                }}>
                                    Active (Ad will be shown to users)
                                </label>
                            </div>

                            {/* Action Buttons */}
                            <div style={{ display: 'flex', gap: '12px', marginTop: '8px' }}>
                                <button
                                    type="submit"
                                    className="btn-add"
                                    style={{ flex: 1, position: 'relative' }}
                                    disabled={isUploading}
                                >
                                    {isUploading ? 'Uploading Media...' : (editingAd ? 'Update Ad' : 'Create Ad')}
                                </button>
                                <button
                                    type="button"
                                    onClick={handleCancel}
                                    disabled={isUploading}
                                    style={{
                                        flex: 1,
                                        padding: '12px 24px',
                                        borderRadius: '12px',
                                        border: '1px solid var(--border-light)',
                                        background: 'var(--bg-secondary)',
                                        color: 'var(--text-primary)',
                                        fontWeight: 600,
                                        cursor: 'pointer'
                                    }}
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        );
    }

    return (
        <div className="content-section fade-in">
            {/* Header with Stats */}
            <div className="content-header">
                <div>
                    <h2 className="content-title">Ad Management</h2>
                    <p style={{ margin: 0, color: 'var(--text-tertiary)', fontSize: '14px' }}>
                        Manage ads that appear every 5 posts in user feeds
                    </p>
                </div>
                <div className="header-actions">
                    <div className="search-bar-mini">
                        <FaSearch className="search-icon-mini" />
                        <input
                            type="text"
                            placeholder="Search ads..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                    <button className="btn-add" onClick={() => setIsCreating(true)}>
                        <FaPlus style={{ marginRight: '8px' }} />
                        Create New Ad
                    </button>
                </div>
            </div>


            {/* Stats Cards */}
            <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '16px',
                marginBottom: '24px'
            }}>
                <div style={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    padding: '20px',
                    borderRadius: '16px',
                    color: 'white'
                }}>
                    <div style={{ fontSize: '13px', opacity: 0.9, marginBottom: '8px' }}>Total Ads</div>
                    <div style={{ fontSize: '32px', fontWeight: 700 }}>{ads.length}</div>
                </div>
                <div style={{
                    background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                    padding: '20px',
                    borderRadius: '16px',
                    color: 'white'
                }}>
                    <div style={{ fontSize: '13px', opacity: 0.9, marginBottom: '8px' }}>Active Ads</div>
                    <div style={{ fontSize: '32px', fontWeight: 700 }}>{activeAds}</div>
                </div>
                <div style={{
                    background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                    padding: '20px',
                    borderRadius: '16px',
                    color: 'white'
                }}>
                    <div style={{ fontSize: '13px', opacity: 0.9, marginBottom: '8px' }}>Total Clicks</div>
                    <div style={{ fontSize: '32px', fontWeight: 700 }}>{totalClicks.toLocaleString()}</div>
                </div>
                <div style={{
                    background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
                    padding: '20px',
                    borderRadius: '16px',
                    color: 'white'
                }}>
                    <div style={{ fontSize: '13px', opacity: 0.9, marginBottom: '8px' }}>Avg CTR</div>
                    <div style={{ fontSize: '32px', fontWeight: 700 }}>{avgCTR}%</div>
                </div>
            </div>

            {/* Ads Table */}
            <div className="table-container">
                <table className="data-table">
                    <thead>
                        <tr>
                            <th style={{ width: '40%' }}>Ad Details</th>
                            <th style={{ width: '15%' }}>Status</th>
                            <th style={{ width: '15%' }}>Performance</th>
                            <th style={{ width: '15%' }}>Duration</th>
                            <th style={{ width: '15%' }}>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredAds.length === 0 ? (
                            <tr>
                                <td colSpan="5" style={{ textAlign: 'center', padding: '40px', color: 'var(--text-tertiary)' }}>
                                    No ads found. Create your first ad to get started!
                                </td>
                            </tr>
                        ) : (
                            filteredAds.map(ad => (
                                <tr key={ad.id}>
                                    <td>
                                        <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                                            <div style={{
                                                width: '120px',
                                                height: '60px',
                                                borderRadius: '8px',
                                                overflow: 'hidden',
                                                border: '1px solid var(--border-light)',
                                                flexShrink: 0,
                                                background: '#000',
                                                position: 'relative'
                                            }}>
                                                {ad.mediaType === 'video' ? (
                                                    <>
                                                        <video
                                                            src={ad.mediaUrl}
                                                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                        />
                                                        <div style={{
                                                            position: 'absolute',
                                                            top: '4px',
                                                            right: '4px',
                                                            background: 'rgba(0,0,0,0.7)',
                                                            color: 'white',
                                                            padding: '2px 6px',
                                                            borderRadius: '4px',
                                                            fontSize: '10px',
                                                            fontWeight: 600
                                                        }}>
                                                            <FaVideo size={8} /> VIDEO
                                                        </div>
                                                    </>
                                                ) : (
                                                    <img
                                                        src={ad.mediaUrl}
                                                        alt={ad.title}
                                                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                    />
                                                )}
                                            </div>
                                            <div>
                                                <div style={{
                                                    fontWeight: 600,
                                                    color: 'var(--text-primary)',
                                                    marginBottom: '4px'
                                                }}>
                                                    {ad.title}
                                                </div>
                                                {ad.description && (
                                                    <div style={{
                                                        fontSize: '12px',
                                                        color: 'var(--text-secondary)',
                                                        marginBottom: '6px',
                                                        lineHeight: '1.4',
                                                        maxWidth: '300px'
                                                    }}
                                                        title={ad.description}
                                                    >
                                                        {ad.description.length > 80
                                                            ? ad.description.substring(0, 80) + '...'
                                                            : ad.description
                                                        }
                                                    </div>
                                                )}
                                                <div style={{
                                                    fontSize: '12px',
                                                    color: 'var(--text-tertiary)',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '4px'
                                                }}>
                                                    <FaLink size={10} />
                                                    {ad.redirectUrl}
                                                </div>
                                                <div style={{
                                                    marginTop: '4px',
                                                    fontSize: '11px',
                                                    color: 'var(--text-tertiary)',
                                                    display: 'flex',
                                                    gap: '8px'
                                                }}>
                                                    <span>📍 {ad.country}</span>
                                                    <span>•</span>
                                                    <span style={{ 
                                                        color: ad.targetGroupId === 'All' ? 'var(--text-tertiary)' : 'var(--primary-blue)',
                                                        fontWeight: ad.targetGroupId === 'All' ? 400 : 600
                                                    }}>
                                                        <FaLayerGroup size={10} style={{ marginRight: '4px' }} />
                                                        {ad.targetGroupId === 'All' 
                                                            ? 'All Communities' 
                                                            : (groups.find(g => g.id === ad.targetGroupId)?.name || 'Unknown Group')
                                                        }
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </td>
                                    <td>
                                        <span style={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: '6px',
                                            padding: '6px 12px',
                                            borderRadius: '8px',
                                            fontSize: '12px',
                                            fontWeight: 600,
                                            background: ad.isActive
                                                ? 'rgba(34, 197, 94, 0.1)'
                                                : 'rgba(239, 68, 68, 0.1)',
                                            color: ad.isActive ? '#22c55e' : '#ef4444'
                                        }}>
                                            {ad.isActive ? <FaEye size={12} /> : <FaEyeSlash size={12} />}
                                            {ad.isActive ? 'Active' : 'Inactive'}
                                        </span>
                                    </td>
                                    <td>
                                        <div style={{ fontSize: '13px' }}>
                                            <div style={{
                                                color: 'var(--text-primary)',
                                                fontWeight: 600,
                                                marginBottom: '2px'
                                            }}>
                                                {(ad.clicks || 0).toLocaleString()} clicks
                                            </div>
                                            <div style={{
                                                color: 'var(--text-tertiary)',
                                                fontSize: '11px'
                                            }}>
                                                {(ad.impressions || 0).toLocaleString()} views
                                            </div>
                                        </div>
                                    </td>
                                    <td>
                                        <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                                            All Time
                                        </div>
                                    </td>
                                    <td>
                                        <div className="action-buttons" style={{ gap: '6px' }}>
                                            <button
                                                className="btn-action view"
                                                title={ad.isActive ? "Deactivate" : "Activate"}
                                                onClick={() => toggleAdStatus(ad)}
                                            >
                                                {ad.isActive ? <FaEyeSlash /> : <FaEye />}
                                            </button>
                                            <button
                                                className="btn-action edit"
                                                title="Edit Ad"
                                                onClick={() => handleEdit(ad)}
                                            >
                                                <FaEdit />
                                            </button>
                                            <button
                                                className="btn-action delete"
                                                title="Delete Ad"
                                                onClick={() => setConfirmModalData({ isOpen: true, adId: ad.id })}
                                            >
                                                <FaTrash />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* Info Box */}
            <div style={{
                marginTop: '24px',
                padding: '20px',
                background: 'var(--bg-secondary)',
                borderRadius: '12px',
                border: '1px solid var(--border-light)'
            }}>
                <h3 style={{
                    fontSize: '14px',
                    fontWeight: 600,
                    color: 'var(--text-primary)',
                    marginBottom: '12px'
                }}>
                    📌 How Ads Work
                </h3>
                <ul style={{
                    margin: 0,
                    paddingLeft: '20px',
                    color: 'var(--text-secondary)',
                    fontSize: '13px',
                    lineHeight: '1.8'
                }}>
                    <li>Ads are automatically inserted <strong>every {adFrequency} posts</strong> in user feeds</li>
                    <li>Only <strong>active ads</strong> within their date range will be shown</li>
                    <li>Ads are randomly selected from the active pool for each insertion</li>
                    <li>Users can click on ads to be redirected to the specified URL</li>
                    <li>Track performance with clicks and impressions metrics</li>
                    <li>Adjust ad frequency in the settings above (currently: every {adFrequency} posts)</li>
                </ul>
            </div>

            <ConfirmModal 
                isOpen={confirmModalData.isOpen}
                onClose={() => setConfirmModalData({ isOpen: false, adId: null })}
                onConfirm={() => handleDelete(confirmModalData.adId)}
                title="Delete Advertisement?"
                message="Are you sure you want to delete this ad? This action will permanently remove it and all its associated metrics."
                confirmText="Yes, Delete Ad"
                type="danger"
            />

            <ConfirmModal 
                isOpen={confirmConfig.isOpen}
                onClose={() => setConfirmConfig({ ...confirmConfig, isOpen: false })}
                onConfirm={confirmConfig.onConfirm}
                title={confirmConfig.title}
                message={confirmConfig.message}
                type={confirmConfig.type}
                singleButton={confirmConfig.singleButton}
                confirmText={confirmConfig.singleButton ? "Got it" : "Confirm"}
            />
        </div>
    );
};

export default AdsModule;